/**
 * PreventiveMaintenancePanel — recurring cyclic task tracker for a property.
 * Renders inside the Property Portal. Chips are color-coded by days-until-due:
 *   green ≥30d, yellow 0-30d, red overdue. Mark-Done bumps the next-due date
 *   automatically by the task's cadence.
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Plus, Check, Trash, Wind, CloudRain, Fire, Drop, HouseLine,
         Bell, Bug, Clock } from "@phosphor-icons/react";

const ICON_MAP = {
  hvac_filter: Wind,
  gutter_clean: CloudRain,
  dryer_vent: Fire,
  water_heater_flush: Drop,
  roof_inspect: HouseLine,
  chimney_sweep: Fire,
  smoke_battery: Bell,
  extinguisher_check: Fire,
  pest_control: Bug,
  custom: Wrench,
};

function dueChip(days) {
  if (days == null) return { txt: "—", cls: "bg-slate-100 text-slate-500 border-slate-300" };
  if (days < 0) return { txt: `${Math.abs(days)}d OVERDUE`, cls: "bg-red-100 text-red-800 border-red-500" };
  if (days <= 30) return { txt: `Due in ${days}d`, cls: "bg-yellow-100 text-yellow-800 border-yellow-500" };
  if (days <= 60) return { txt: `Due in ${days}d`, cls: "bg-cyan-100 text-cyan-800 border-cyan-500" };
  return { txt: `${days}d away`, cls: "bg-emerald-50 text-emerald-700 border-emerald-300" };
}

export default function PreventiveMaintenancePanel({ address }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [presets, setPresets] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ task_type: "hvac_filter", task_label: "",
                                        cadence_months: 3, last_done_at: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pm-schedule", { params: { address } });
      setTasks(r.data.tasks || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!address) return;
    load();
    api.get("/pm-schedule/presets").then((r) => setPresets(r.data.presets || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const pickPreset = (task_type) => {
    const p = presets.find((x) => x.task_type === task_type);
    if (!p) return;
    setDraft((d) => ({ ...d, task_type, task_label: p.label,
                       cadence_months: p.cadence_months }));
  };

  const create = async () => {
    try {
      await api.post("/pm-schedule", { address, ...draft });
      toast.success("Task added");
      setAddOpen(false);
      setDraft({ task_type: "hvac_filter", task_label: "", cadence_months: 3, last_done_at: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const markDone = async (t) => {
    try {
      await api.post(`/pm-schedule/${t.id}/mark-done`, { note: "" });
      toast.success("Marked done — next cycle scheduled");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (t) => {
    if (!window.confirm(`Delete "${t.task_label}"?`)) return;
    try {
      await api.delete(`/pm-schedule/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-emerald-500"
         data-testid="pm-panel">
      <div className="flex items-center gap-2">
        <Wrench weight="fill" size={20} className="text-emerald-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Preventive Maintenance</h3>
        <span className="text-xs text-slate-500 font-bold">· {tasks.length}</span>
        <button onClick={() => setAddOpen(true)} data-testid="pm-add-btn"
                className="ml-auto h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Plus weight="bold" size={12}/> Add Task
        </button>
      </div>

      {loading && <div className="mt-3 text-xs text-slate-500 font-bold">Loading…</div>}
      {!loading && tasks.length === 0 && (
        <div className="mt-3 p-4 border-2 border-dashed border-slate-300 bg-slate-50 text-center"
             data-testid="pm-empty">
          <Wrench size={30} className="mx-auto text-slate-400 mb-1"/>
          <div className="text-xs font-bold text-slate-500">No preventive maintenance scheduled yet.</div>
          <div className="text-[10px] text-slate-400 mt-1">
            Add HVAC filter changes, gutter cleans, dryer vent inspections — cron auto-drafts WOs when due.
          </div>
          <button onClick={() => setAddOpen(true)} data-testid="pm-empty-add"
                  className="mt-3 h-8 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
            <Plus weight="bold" size={12}/> Add first task
          </button>
        </div>
      )}

      {!loading && tasks.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {tasks.map((t) => {
            const Icon = ICON_MAP[t.task_type] || Wrench;
            const chip = dueChip(t.days_until_due);
            return (
              <div key={t.id} className="flex items-center gap-2 p-2 bg-slate-50 border-2 border-slate-200 hover:border-emerald-500"
                   data-testid={`pm-task-${t.id}`}>
                <Icon size={22} className="text-emerald-600 flex-shrink-0"/>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold text-slate-800 truncate">
                    {t.task_label}
                    {t.vendor_name && <span className="ml-2 text-[10px] text-slate-500">via <b>{t.vendor_name}</b></span>}
                  </div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>Every {t.cadence_months}mo</span>
                    {t.last_done_at && <span>· Last: {new Date(t.last_done_at).toLocaleDateString()}</span>}
                    <span>· Next: {t.next_due_at ? new Date(t.next_due_at).toLocaleDateString() : "—"}</span>
                    {t.auto_wo_id && <span className="text-emerald-700 font-black">· WO drafted</span>}
                  </div>
                </div>
                <span className={`px-2 py-1 border-2 font-black uppercase text-[9px] tracking-widest ${chip.cls}`}
                      data-testid={`pm-chip-${t.id}`}>
                  {chip.txt}
                </span>
                <button onClick={() => markDone(t)} data-testid={`pm-done-${t.id}`}
                        title="Mark done — bumps next cycle"
                        className="h-8 w-8 flex items-center justify-center border-2 border-emerald-500 hover:bg-emerald-50 text-emerald-700">
                  <Check weight="bold" size={14}/>
                </button>
                <button onClick={() => del(t)} data-testid={`pm-del-${t.id}`}
                        className="h-8 w-8 flex items-center justify-center border-2 border-transparent hover:border-red-500 hover:bg-red-50 text-red-600">
                  <Trash weight="bold" size={14}/>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Task Modal */}
      {addOpen && (
        <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
             onClick={() => setAddOpen(false)}>
          <div className="bg-white w-full max-w-md border-4 border-emerald-500 shadow-[6px_6px_0_#0f172a]"
               onClick={(e) => e.stopPropagation()} data-testid="pm-add-modal">
            <div className="bg-emerald-600 text-white p-4">
              <div className="text-[10px] font-black uppercase tracking-[.2em] opacity-70">Preventive Maintenance</div>
              <h2 className="text-xl font-black mt-1">Add Task</h2>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Preset</label>
                <div className="mt-1 grid grid-cols-2 gap-1.5">
                  {presets.map((p) => (
                    <button key={p.task_type} onClick={() => pickPreset(p.task_type)}
                            data-testid={`pm-preset-${p.task_type}`}
                            className={`h-11 px-2 border-2 text-left font-bold text-xs tracking-tight
                              ${draft.task_type === p.task_type
                                ? "bg-emerald-600 text-white border-emerald-700"
                                : "bg-white text-slate-800 border-slate-300 hover:border-emerald-500"}`}>
                      {p.label}
                      <div className="text-[9px] opacity-70 font-normal">every {p.cadence_months}mo</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Custom label (optional)</label>
                <input value={draft.task_label} onChange={(e) => setDraft({ ...draft, task_label: e.target.value })}
                       placeholder="e.g., HVAC Filter — 1st Floor Unit"
                       data-testid="pm-label-input"
                       className="mt-1 w-full h-11 px-3 border-2 border-slate-300 font-medium"/>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Cadence (months)</label>
                  <input type="number" min="1" max="60" value={draft.cadence_months}
                         onChange={(e) => setDraft({ ...draft, cadence_months: parseInt(e.target.value, 10) || 1 })}
                         data-testid="pm-cadence-input"
                         className="mt-1 w-full h-11 px-3 border-2 border-slate-300 font-black"/>
                </div>
                <div>
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Last done (optional)</label>
                  <input type="date" value={draft.last_done_at ? draft.last_done_at.slice(0, 10) : ""}
                         onChange={(e) => setDraft({ ...draft, last_done_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                         data-testid="pm-lastdone-input"
                         className="mt-1 w-full h-11 px-3 border-2 border-slate-300 font-medium"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-600">Notes (optional)</label>
                <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                          rows={2} placeholder="Anything useful for the tech"
                          data-testid="pm-notes-input"
                          className="mt-1 w-full p-2 border-2 border-slate-300 text-sm font-medium"/>
              </div>
            </div>
            <div className="p-3 border-t-2 border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
              <button onClick={() => setAddOpen(false)}
                      className="h-10 px-4 border-2 border-slate-400 bg-white font-black uppercase text-xs tracking-widest">
                Cancel
              </button>
              <button onClick={create} data-testid="pm-create-confirm"
                      className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest inline-flex items-center gap-2 shadow-[3px_3px_0_#0f172a]">
                <Plus weight="bold" size={14}/> Add Task
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
